import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { CheckCircle2, Coins, MessageCircle, Mail, Banknote, CreditCard, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

const PAYMENTS = [
  { value: "cash",         icon: Banknote,   key: "payment_cash" },
  { value: "ideal",        icon: CreditCard, key: "payment_ideal" },
  { value: "bank_transfer",icon: Building2,  key: "payment_bank" },
];

export default function Book() {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [settings, setSettings] = useState({ signal_number: "+31633136379", whatsapp_number: "+31633136379", iban: "", iban_holder: "Isa Westervoorde", bank_name: "Triodos Bank" });
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    setup_type: "chair_at_home",
    preferred_language: lang,
    date: "",
    time: "",
    payment_method: "cash",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/availability").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const dates = useMemo(() => {
    const map = {};
    for (const s of slots) {
      map[s.date] = map[s.date] || [];
      map[s.date].push(s);
    }
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b));
  }, [slots]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) {
      toast.error(t("booking.choose_slot"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setSuccess(true);
      toast.success(t("book.success_title"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      // refresh slots in case one was just taken
      api.get("/availability").then((r) => setSlots(r.data)).catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="book-success" className="container-refined py-24 max-w-2xl">
        <CheckCircle2 className="h-12 w-12 text-wheat-600" strokeWidth={1.25} />
        <h1 className="mt-6 font-serif text-4xl text-forest-900">{t("book.success_title")}</h1>
        <p className="mt-4 text-forest-700 leading-relaxed">{t("book.success_body")} You&apos;ll receive a confirmation email at {form.email}.</p>
        <div className="mt-8 flex gap-3 flex-wrap">
          <a href={`https://signal.me/#p/${(settings.signal_number || "+31633136379").replace(/[^+0-9]/g,"")}`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="book-signal-link">
            <MessageCircle className="h-4 w-4" strokeWidth={1.5}/> Signal
          </a>
          <Link to="/fundraiser" className="btn-ghost-link" data-testid="book-see-proofs">{t("pricing.see_proof")}</Link>
        </div>
      </div>
    );
  }

  const signalHref = `https://signal.me/#p/${(settings.signal_number || "+31633136379").replace(/[^+0-9]/g,"")}`;
  const waHref = `https://wa.me/${(settings.whatsapp_number || "+31633136379").replace(/[^0-9]/g,"").replace(/^0/, "31")}?text=${encodeURIComponent("Hoi Isa, ik heb een vraag over een Reiki-sessie.")}`;

  return (
    <div data-testid="book-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow">{t("book.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("book.title")}</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">{t("book.lede")}</p>
        <p className="mt-4 text-sm text-forest-700 leading-relaxed" data-testid="book-duration-note">{t("booking.duration_note")}</p>
        <div className="mt-4 rounded-2xl border border-wheat-500/40 bg-wheat-50/60 p-4 text-sm text-forest-800" data-testid="book-travel-note">
          {t("booking.travel_note")}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-wheat-50 border border-wheat-500/30 px-4 py-2 text-xs text-forest-800" data-testid="book-ukraine-note">
            <Coins className="h-4 w-4 text-wheat-700" strokeWidth={1.5} />
            <span>{t("pricing.send_note")}</span>
            <Link to="/fundraiser" className="ml-2 underline underline-offset-4">{t("pricing.see_proof")}</Link>
          </div>
          <a href={signalHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-forest-900/15 bg-white/70 px-4 py-2 text-xs text-forest-800 hover:border-wheat-500/60 transition" data-testid="book-signal-help">
            <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5}/> Signal
          </a>
          <a href={waHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-forest-900/15 bg-white/70 px-4 py-2 text-xs text-forest-800 hover:border-wheat-500/60 transition" data-testid="book-whatsapp-help">
            <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5}/> WhatsApp
          </a>
        </div>
      </div>

      {services.length > 0 && (
        <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="book-services-list">
          {services.map((s) => (
            <div key={s.id} className="card-warm !p-6" data-testid={`book-service-${s.setup_type}`}>
              <div className="flex items-baseline justify-between">
                <p className="eyebrow">{s.duration_minutes} min</p>
                <p className="font-serif text-2xl text-forest-900">€{Number(s.price).toFixed(0)}</p>
              </div>
              <h3 className="mt-2 font-serif text-xl text-forest-900">{s.name}</h3>
              <p className="mt-2 text-xs text-forest-700 leading-relaxed">{s.description}</p>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-wheat-700">80% → Ukraine · 20% → Isa</p>
            </div>
          ))}
        </div>
      )}

      {/* Availability picker */}
      <section className="mt-12" data-testid="book-slot-picker">
        <p className="eyebrow">{t("booking.choose_slot")}</p>
        {dates.length === 0 ? (
          <div className="card-warm mt-4" data-testid="book-no-slots">
            <p className="font-serif text-2xl text-forest-900">{t("booking.no_slots")}</p>
            <p className="mt-3 text-sm text-forest-700">{t("booking.bring_light")}</p>
            <a href={signalHref} target="_blank" rel="noreferrer" className="btn-secondary mt-6" data-testid="book-no-slots-signal">
              <MessageCircle className="h-4 w-4" strokeWidth={1.5}/> Signal
            </a>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {dates.map(([date, times]) => (
              <div key={date} data-testid={`book-date-${date}`}>
                <p className="text-sm font-semibold text-forest-800">
                  {new Date(date + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : lang === "nl" ? "nl-NL" : "uk-UA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {times.map((slot) => {
                    const selected = form.date === slot.date && form.time === slot.time;
                    return (
                      <button
                        type="button"
                        key={slot.id}
                        onClick={() => setForm({ ...form, date: slot.date, time: slot.time })}
                        data-testid={`book-slot-${slot.date}-${slot.time}`}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold tracking-wide transition ${
                          selected
                            ? "border-forest-800 bg-forest-800 text-ivory"
                            : "border-forest-900/15 bg-white/70 text-forest-800 hover:border-wheat-500/60"
                        }`}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {dates.length > 0 && (
        <form onSubmit={submit} className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl" data-testid="book-form">
          <div>
            <label className="field-label">{t("book.name")}</label>
            <input required data-testid="book-name" className="field-input" value={form.customer_name} onChange={onChange("customer_name")} />
          </div>
          <div>
            <label className="field-label">{t("book.email")}</label>
            <input required type="email" data-testid="book-email" className="field-input" value={form.email} onChange={onChange("email")} />
          </div>
          <div>
            <label className="field-label">{t("book.phone")}</label>
            <input required data-testid="book-phone" className="field-input" value={form.phone} onChange={onChange("phone")} />
          </div>
          <div>
            <label className="field-label">{t("book.lang")}</label>
            <select data-testid="book-language" className="field-input" value={form.preferred_language} onChange={onChange("preferred_language")}>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
              <option value="uk">Українська</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="field-label">{t("book.address")}</label>
            <input required data-testid="book-address" className="field-input" value={form.address} onChange={onChange("address")} />
          </div>

          {/* Payment method */}
          <div className="md:col-span-2">
            <label className="field-label">{t("booking.payment_title")}</label>
            <div className="grid md:grid-cols-3 gap-3">
              {PAYMENTS.map((p) => (
                <label
                  key={p.value}
                  data-testid={`book-payment-${p.value}`}
                  className={`cursor-pointer rounded-2xl border p-4 text-sm transition ${
                    form.payment_method === p.value
                      ? "border-wheat-500 bg-wheat-50/60 text-forest-900"
                      : "border-forest-900/15 bg-white/60 text-forest-700 hover:border-wheat-500/50"
                  }`}
                >
                  <input type="radio" name="payment" className="sr-only" value={p.value} checked={form.payment_method === p.value} onChange={onChange("payment_method")} />
                  <p.icon className="h-5 w-5" strokeWidth={1.5} />
                  <span className="mt-2 block font-semibold">{t(`booking.${p.key}`)}</span>
                </label>
              ))}
            </div>
            {form.payment_method !== "cash" && (
              <div className="mt-3 rounded-xl border border-forest-900/10 bg-ivory/70 p-4 text-sm text-forest-800" data-testid="book-payment-details">
                <p className="font-semibold">{settings.bank_name || "Triodos Bank"} — {settings.iban_holder || "Isa Westervoorde"}</p>
                <p className="mt-1">{settings.iban ? `IBAN: ${settings.iban}` : t("booking.payment_note_default")}</p>
                {settings.payment_note && <p className="mt-1 text-forest-700">{settings.payment_note}</p>}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="field-label">{t("book.notes")}</label>
            <textarea rows={4} data-testid="book-notes" className="field-input" value={form.notes} onChange={onChange("notes")} />
          </div>

          {form.date && form.time && (
            <div className="md:col-span-2 rounded-2xl border border-wheat-500/40 bg-wheat-50/50 p-4 text-sm text-forest-800" data-testid="book-slot-summary">
              You&apos;re booking <strong>{new Date(form.date + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : lang === "nl" ? "nl-NL" : "uk-UA", { day: "numeric", month: "long", year: "numeric" })}</strong> at <strong>{form.time}</strong>.
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
            <button type="submit" disabled={submitting || !form.date || !form.time} className="btn-primary" data-testid="book-submit">
              {submitting ? t("common.loading") : t("book.submit")}
            </button>
            <a href={signalHref} target="_blank" rel="noreferrer" className="btn-ghost-link" data-testid="book-signal-inline">
              <MessageCircle className="h-4 w-4" strokeWidth={1.5}/> Signal
            </a>
            <a href={waHref} target="_blank" rel="noreferrer" className="btn-ghost-link" data-testid="book-whatsapp-inline">
              <MessageCircle className="h-4 w-4" strokeWidth={1.5}/> WhatsApp
            </a>
            <a href="mailto:info@sunbraider.nl" className="btn-ghost-link" data-testid="book-email-inline">
              <Mail className="h-4 w-4" strokeWidth={1.5}/> info@sunbraider.nl
            </a>
          </div>
        </form>
      )}
    </div>
  );
}
